import React, { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Bot } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API, getToken } from "../lib/api";

const SESSION_KEY = "ss_assistant_session";

const getSession = () => {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = "sess_" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
};

export const Assistant = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(getSession());
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: "assistant",
        content: "Hi! I'm Sprout 🌱 — your school-supply assistant. Tell me who you're shopping for (parent for a grade-3 kid? teacher stocking a classroom?) and I'll suggest the perfect picks.",
      }]);
    }
  }, [open, messages.length]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || streaming) return;
    const userMsg = { role: "user", content: input.trim() };
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);

    try {
      const token = getToken();
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId.current, message: userMsg.content }),
      });
      if (!res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const p of parts) {
          const line = p.replace(/^data:\s?/, "");
          if (line === "[DONE]") continue;
          if (line.startsWith("[error")) {
            setMessages((m) => { const nx = [...m]; nx[nx.length - 1] = { role: "assistant", content: "Sorry — I had a hiccup. Try again in a moment." }; return nx; });
            continue;
          }
          setMessages((m) => {
            const nx = [...m];
            nx[nx.length - 1] = { role: "assistant", content: (nx[nx.length - 1].content || "") + line };
            return nx;
          });
        }
      }
    } catch (err) {
      setMessages((m) => { const nx = [...m]; nx[nx.length - 1] = { role: "assistant", content: "Sorry — couldn't reach the assistant. Please try again." }; return nx; });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="assistant-open-btn"
          className="fixed bottom-6 right-6 z-40 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 text-white shadow-xl shadow-indigo-500/30 pl-4 pr-5 py-3 flex items-center gap-2 hover:-translate-y-0.5 active:scale-95 transition-all">
          <Sparkles className="w-4 h-4" />
          <span className="font-semibold text-sm">Ask Sprout AI</span>
        </button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[min(400px,calc(100vw-2rem))] h-[600px] max-h-[calc(100vh-3rem)] bg-white rounded-3xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="assistant-panel">
          <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-indigo-600 to-purple-600 text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"><Bot className="w-4 h-4" /></div>
              <div>
                <div className="font-display font-bold">Sprout AI</div>
                <div className="text-[10px] text-white/80">Powered by Claude Sonnet</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="assistant-close-btn" className="p-1 rounded-full hover:bg-white/10"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-indigo-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}
                     data-testid={`assistant-msg-${m.role}-${i}`}>
                  {m.content || <span className="inline-block w-1.5 h-4 bg-slate-400 animate-pulse rounded" />}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={send} className="p-3 border-t border-slate-100 flex gap-2 bg-white">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={streaming ? "Sprout is thinking…" : "Ask about supplies…"}
              disabled={streaming}
              className="rounded-full"
              data-testid="assistant-input"
            />
            <Button type="submit" size="icon" disabled={streaming || !input.trim()} className="rounded-full bg-indigo-600 hover:bg-indigo-700 shrink-0" data-testid="assistant-send-btn">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
};
